import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  CommerceCatalogWorkspace,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  DescriptionText,
  HorizontalStack,
  RegistryStatus,
  toast,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import {
  MapPin,
  Pencil,
} from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import {
  createAccessPointLayer,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listAccessPointLayers,
  updateAccessPointLayer,
  type AccessPointLayer,
} from '@/lib/commerce';
import { listMapSources, type MapSource } from '@/lib/mapping-api';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';
import {
  lastPageIndexForTotal,
  toLimitOffset,
  type RoutePaginationState,
} from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import {
  AccessPointsWizardEditor,
  type AccessPointLayerFormState,
} from './AccessPointsWizardEditor';
import {
  hydrateRoutePricingBuildTypeRates,
  parseRoutePricingBuildTypeRates,
} from '@/features/commerce-admin/routePricingRateController';

const defaultPagination: RoutePaginationState = { pageIndex: 0, pageSize: 10 };
const allowedPageSizes = new Set([10, 25, 50, 100]);
const allowedStatuses = new Set(['draft', 'active', 'archived']);
const allowedPurposes = new Set(['business_quote_estimate', 'quote_estimate', 'serviceability', 'install_feasibility']);

interface AccessPointsRouteState {
  search: string;
  statuses: string[];
  purposes: string[];
  sort: string[];
  pagination: RoutePaginationState;
}
const ACCESS_POINT_SORT_FIELDS = ['created_at', 'name', 'purpose', 'source', 'status', 'updated_at'] as const;
const allowedAccessPointSortFields = new Set<string>(ACCESS_POINT_SORT_FIELDS);

function parsePositiveInteger(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function parseAccessPointsRouteState(path: string): AccessPointsRouteState {
  const queryIndex = path.indexOf('?');
  const params = new URLSearchParams(queryIndex >= 0 ? path.slice(queryIndex + 1) : '');
  const requestedPageSize = parsePositiveInteger(params.get('page_size'), defaultPagination.pageSize);
  const pageSize = allowedPageSizes.has(requestedPageSize) ? requestedPageSize : defaultPagination.pageSize;
  return {
    search: params.get('q')?.trim() ?? '',
    statuses: Array.from(new Set(params.getAll('status').filter(value => allowedStatuses.has(value)))).sort(),
    purposes: Array.from(new Set(params.getAll('purpose').filter(value => allowedPurposes.has(value)))).sort(),
    sort: params.getAll('sort').map(value => value.trim()).filter(value => (
      allowedAccessPointSortFields.has(value.startsWith('-') ? value.slice(1) : value)
    )),
    pagination: {
      pageIndex: parsePositiveInteger(params.get('page'), 1) - 1,
      pageSize,
    },
  };
}

function buildAccessPointsRoute(state: AccessPointsRouteState): string {
  const params = new URLSearchParams();
  const search = state.search.trim();
  if (search) params.set('q', search);
  state.statuses.slice().sort().forEach(status => params.append('status', status));
  state.purposes.slice().sort().forEach(purpose => params.append('purpose', purpose));
  state.sort.forEach(value => params.append('sort', value));
  if (state.pagination.pageIndex > 0) params.set('page', String(state.pagination.pageIndex + 1));
  if (state.pagination.pageSize !== defaultPagination.pageSize) {
    params.set('page_size', String(state.pagination.pageSize));
  }
  const query = params.toString();
  return `/commerce/access-points${query ? `?${query}` : ''}`;
}

function formatJSONObject(value: Record<string, unknown> | undefined): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJSONObject(value: string, label: string): Record<string, unknown> {
  const trimmed = value.trim();
  if (!trimmed) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
}

function createEmptyLayerForm(): AccessPointLayerFormState {
  return {
    layerKey: '',
    name: '',
    accessPointType: 'fiber',
    purpose: 'business_quote_estimate',
    status: 'draft',
    mappingSourceId: '',
    defaultBuildTypeRates: [],
    metadataJSON: formatJSONObject({}),
  };
}

function buildLayerForm(item: AccessPointLayer): AccessPointLayerFormState {
  return {
    layerKey: item.layer_key || '',
    name: item.name || '',
    accessPointType: item.access_point_type || 'fiber',
    purpose: item.purpose || 'business_quote_estimate',
    status: item.status || 'draft',
    mappingSourceId: item.mapping_source_id || '',
    defaultBuildTypeRates: hydrateRoutePricingBuildTypeRates(item.default_build_type_rates),
    metadataJSON: formatJSONObject(item.metadata),
  };
}

function buildLayerPayload(form: AccessPointLayerFormState): Record<string, unknown> {
  const parsedRates = parseRoutePricingBuildTypeRates(form.defaultBuildTypeRates, { label: 'Default route pricing' });
  if (parsedRates.validationIssues[0]) throw new Error(parsedRates.validationIssues[0].message);
  return {
    layer_key: form.layerKey.trim(),
    name: form.name.trim(),
    access_point_type: form.accessPointType,
    purpose: form.purpose,
    status: form.status,
    mapping_source_id: form.mappingSourceId || undefined,
    default_build_type_rates: parsedRates.rates,
    metadata: parseJSONObject(form.metadataJSON, 'Layer metadata'),
  };
}

function formatRelativeDate(value?: string | null): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function StatusBadge({ label, tone }: { label: string; tone: 'success' | 'info' | 'warning' | 'error' | 'muted' | 'default' }) {
  return <RegistryStatus label={label} variant={tone} />;
}

function statusTone(status: string): 'success' | 'warning' | 'muted' {
  if (status === 'active') return 'success';
  if (status === 'draft') return 'warning';
  return 'muted';
}

function applyPaginationUpdate(
  current: RoutePaginationState,
  updater: RoutePaginationState | ((value: RoutePaginationState) => RoutePaginationState),
): RoutePaginationState {
  const next = typeof updater === 'function' ? updater(current) : updater;
  if (next.pageSize !== current.pageSize) {
    return { ...next, pageIndex: 0 };
  }
  return next;
}

export default function AccessPointsPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const requestVersionRef = useRef(0);
  const routeState = useMemo(() => parseAccessPointsRouteState(navigation.path), [navigation.path]);
  const searchQuery = routeState.search;
  const selectedStatuses = routeState.statuses;
  const selectedPurposes = routeState.purposes;
  const pagination = routeState.pagination;
  const sorting = useMemo<SortingState>(() => routeState.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [routeState.sort]);

  const updateRouteState = useCallback((
    patch: Partial<AccessPointsRouteState>,
    options?: { replace?: boolean },
  ) => {
    navigation.navigate(buildAccessPointsRoute({
      ...routeState,
      ...patch,
      pagination: patch.pagination ?? routeState.pagination,
      statuses: patch.statuses ?? routeState.statuses,
      purposes: patch.purposes ?? routeState.purposes,
      sort: patch.sort ?? routeState.sort,
    }), options);
  }, [navigation, routeState]);

  useEffect(() => {
    const canonicalRoute = buildAccessPointsRoute(routeState);
    if (navigation.path !== canonicalRoute) {
      navigation.navigate(canonicalRoute, { replace: true });
    }
  }, [navigation, routeState]);

  const [layers, setLayers] = useState<AccessPointLayer[]>([]);
  const [mapSources, setMapSources] = useState<MapSource[]>([]);
  const [totalLayers, setTotalLayers] = useState(0);
  const [activeLayerCount, setActiveLayerCount] = useState(0);
  const [businessLayerCount, setBusinessLayerCount] = useState(0);
  const [facetBuckets, setFacetBuckets] = useState<Record<string, Array<{ value: string; label?: string | null; count: number }>>>({});
  const statusOptions = useMemo(() => {
    const buckets = new Map((facetBuckets.status ?? []).map(bucket => [bucket.value, bucket]));
    selectedStatuses.forEach(value => {
      if (!buckets.has(value)) buckets.set(value, { value, label: null, count: 0 });
    });
    return Array.from(buckets.values()).map(bucket => ({
      label: bucket.label || bucket.value.charAt(0).toUpperCase() + bucket.value.slice(1),
      value: bucket.value,
    }));
  }, [facetBuckets.status, selectedStatuses]);
  const purposeOptions = useMemo(() => {
    const buckets = new Map((facetBuckets.purpose ?? []).map(bucket => [bucket.value, bucket]));
    selectedPurposes.forEach(value => {
      if (!buckets.has(value)) buckets.set(value, { value, label: null, count: 0 });
    });
    return Array.from(buckets.values()).map(bucket => ({
      label: bucket.label || bucket.value.replaceAll('_', ' '),
      value: bucket.value,
    }));
  }, [facetBuckets.purpose, selectedPurposes]);
  const statusCounts = useMemo(
    () => Object.fromEntries(statusOptions.map(option => [
      option.value,
      (facetBuckets.status ?? []).find(bucket => bucket.value === option.value)?.count ?? 0,
    ])),
    [facetBuckets.status, statusOptions],
  );
  const purposeCounts = useMemo(
    () => Object.fromEntries(purposeOptions.map(option => [
      option.value,
      (facetBuckets.purpose ?? []).find(bucket => bucket.value === option.value)?.count ?? 0,
    ])),
    [facetBuckets.purpose, purposeOptions],
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [layerForm, setLayerForm] = useState<AccessPointLayerFormState>(createEmptyLayerForm());

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const loadData = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current;
    if (!activeTenant?.id) {
      setLayers([]);
      setMapSources([]);
      setTotalLayers(0);
      setActiveLayerCount(0);
      setBusinessLayerCount(0);
      setFacetBuckets({});
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const [page, activePage, businessPage, sources] = await Promise.all([
        listAccessPointLayers({
          catalogOwnerTenantId: activeTenant.id,
          search: searchQuery || undefined,
          status: selectedStatuses,
          purpose: selectedPurposes,
          sort: routeState.sort,
          include_facets: ['status', 'purpose'],
          ...toLimitOffset(pagination),
        }),
        listAccessPointLayers({ catalogOwnerTenantId: activeTenant.id, status: 'active', limit: 1, offset: 0 }),
        listAccessPointLayers({ catalogOwnerTenantId: activeTenant.id, purpose: 'business_quote_estimate', limit: 1, offset: 0 }),
        listMapSources({ limit: 200 }),
      ]);
      if (requestVersion !== requestVersionRef.current) return;
      const nextPageIndex = lastPageIndexForTotal(page.total_count, pagination.pageSize);
      if (pagination.pageIndex > nextPageIndex) {
        updateRouteState({ pagination: { ...pagination, pageIndex: nextPageIndex } }, { replace: true });
        return;
      }
      setLayers(page.items);
      setMapSources(sources);
      setTotalLayers(page.total_count);
      setActiveLayerCount(activePage.total_count);
      setBusinessLayerCount(businessPage.total_count);
      setFacetBuckets(page.facets);
    } catch (err) {
      if (requestVersion !== requestVersionRef.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load access point layers.'));
      }
      setLayers([]);
      setMapSources([]);
      setTotalLayers(0);
      setActiveLayerCount(0);
      setBusinessLayerCount(0);
      setFacetBuckets({});
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  }, [
    activeTenant?.id,
    pagination,
    searchQuery,
    selectedPurposes,
    selectedStatuses,
    routeState.sort,
    updateRouteState,
  ]);

  useEffect(() => { void loadData(); }, [loadData]);

  useEffect(() => {
    setRowSelection({});
  }, [activeTenant?.id]);

  useEffect(() => {
    setRowSelection({});
  }, [searchQuery, selectedPurposes, selectedStatuses]);

  const openCreate = () => {
    setEditingId(null);
    setLayerForm(createEmptyLayerForm());
    setEditorOpen(true);
  };

  const openEdit = useCallback((item: AccessPointLayer) => {
    setEditingId(item.layer_id);
    setLayerForm(buildLayerForm(item));
    setEditorOpen(true);
  }, []);

  const saveLayer = async () => {
    if (!activeTenant?.id) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { catalog_owner_tenant_id: activeTenant.id, ...buildLayerPayload(layerForm) };
      const saved = editingId
        ? await updateAccessPointLayer(editingId, payload)
        : await createAccessPointLayer(payload);
      setEditingId(saved.layer_id);
      setLayerForm(buildLayerForm(saved));
      toast.success(editingId ? 'Access point layer updated' : 'Access point layer created');
      await loadData();
      if (!editingId) {
        setEditorOpen(true);
      }
    } catch (err) {
      const msg = getCommerceErrorMessage(err, 'Failed to save access point layer.');
      setError(msg);
      toast.error('Failed to save', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const metrics = useMemo(() => [
    { id: 'layers', label: 'Layers', value: totalLayers },
    { id: 'active', label: 'Active', value: activeLayerCount },
    { id: 'business', label: 'Business Estimate', value: businessLayerCount },
    { id: 'sources', label: 'Map Sources', value: mapSources.length },
  ], [activeLayerCount, businessLayerCount, mapSources.length, totalLayers]);

  const baseShellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'access_points',
    searchQuery: '',
    searchPlaceholder: 'Search access point layers...',
    onSearchChange: () => undefined,
    error,
    blockingReason: 'Your current role does not have permission to manage access point layers.',
    emptyStateLabel: 'Select a tenant to manage access point layers.',
    onSelectSection: id => navigation.navigate(buildCommerceSectionRoute(id)),
    primaryAction: {
      id: 'create-access-point-layer',
      label: 'New Layer',
      onAction: openCreate,
      disabled: !activeTenant?.id || Boolean(accessDeniedStatus),
    },
    metrics: metrics.map(metric => ({ id: metric.id, label: metric.label, value: String(metric.value) })),
  });
  const shellViewModel = {
    ...baseShellViewModel,
    toolbar: { ...baseShellViewModel.toolbar, showSearch: false },
  };

  const handlePaginationChange = (updater: RoutePaginationState | ((value: RoutePaginationState) => RoutePaginationState)) => {
    updateRouteState({ pagination: applyPaginationUpdate(pagination, updater) });
  };

  const handleSortingChange = (updater: SortingState | ((value: SortingState) => SortingState)) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    updateRouteState({
      sort: next.map(item => `${item.desc ? '-' : ''}${item.id}`),
      pagination: { ...pagination, pageIndex: 0 },
    });
  };

  const handleSearchChange = (value: string) => {
    updateRouteState({
      search: value.trim(),
      pagination: { ...pagination, pageIndex: 0 },
    }, { replace: true });
  };

  const columns = useMemo<ColumnDef<AccessPointLayer>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 320,
      header: 'Layer',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <button
            type="button"
            onClick={() => openEdit(item)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${item.name}`}
          >
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
                {item.name}
              </p>
              <StatusBadge label={item.status} tone={statusTone(item.status)} />
            </div>
            <DescriptionText className="text-[11px]">
              {item.layer_key} • v{item.active_version}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      size: 190,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.purpose.replaceAll('_', ' ')}</span>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 110,
      cell: ({ row }) => <StatusBadge label={row.original.status} tone={statusTone(row.original.status)} />,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'source',
      accessorKey: 'mapping_source_id',
      header: 'Map Source',
      size: 220,
      cell: ({ row }) => {
        const source = mapSources.find(item => item.id === row.original.mapping_source_id);
        return (
          <div className="min-w-0">
            <div className="truncate text-[11px] font-black text-foreground">
              {source?.name || row.original.mapping_source_id || 'Unbound'}
            </div>
            <DescriptionText className="text-[11px]">
              {source?.source_kind || 'Select a shared source'}
            </DescriptionText>
          </div>
        );
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 140,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {formatRelativeDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => openEdit(row.original)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`Modify ${row.original.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [mapSources, openEdit]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: ACCESS_POINT_SORT_FIELDS, registry: 'access-points' });

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      takeoverActive={editorOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={
        <AccessPointsWizardEditor
          isOpen={editorOpen}
          editingId={editingId}
          layerForm={layerForm}
          availableMapSources={mapSources}
          setLayerForm={setLayerForm}
          onSave={saveLayer}
          onClose={() => setEditorOpen(false)}
          saving={saving}
        />
      }
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Access Points' }]}
          title="Access Points"
          description="Bind commerce routing estimates to tenant map sources."
        />
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={layers}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            manualPagination={true}
            totalRowCount={totalLayers}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination={true}
            emptyState={
              <div className="px-0 py-12 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted/30">
                  <MapPin className="h-6 w-6 text-muted-foreground/60" />
                </div>
                <p className="text-sm font-black text-foreground">No Access Point Layers</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || selectedStatuses.length > 0 || selectedPurposes.length > 0
                    ? 'No access point layers matched the current search or filters.'
                    : 'Create a layer to bind commerce routing estimates to a tenant map source.'}
                </DescriptionText>
              </div>
            }
            renderToolbar={(table) => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                searchDebounceMs={250}
                searchPlaceholder="Search access point layers..."
                resultCount={totalLayers}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <HorizontalStack gap={2}>
                    {table.getColumn('status') && (
                      <DataTableFacetedFilter
                        title="Status"
                        selectedValues={new Set(selectedStatuses)}
                        onFilterChange={(values) => updateRouteState({
                          statuses: Array.from(values),
                          pagination: { ...pagination, pageIndex: 0 },
                        })}
                        options={statusOptions}
                        counts={statusCounts}
                      />
                    )}
                    {table.getColumn('purpose') && (
                      <DataTableFacetedFilter
                        title="Purpose"
                        selectedValues={new Set(selectedPurposes)}
                        onFilterChange={(values) => updateRouteState({
                          purposes: Array.from(values),
                          pagination: { ...pagination, pageIndex: 0 },
                        })}
                        options={purposeOptions}
                        counts={purposeCounts}
                      />
                    )}
                  </HorizontalStack>
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
